import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Paper, Typography, Button, Chip, CircularProgress,
  ToggleButtonGroup, ToggleButton, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Avatar, LinearProgress,
} from "@mui/material";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import useAuth from "../hooks/useAuth";

const API = process.env.REACT_APP_API_URL || "";

// CSS Grid helper — reemplaza MUI Grid para garantizar ancho completo sin negative margins
const Row = ({ cols, gap = 16, children, style = {} }) => (
  <div style={{
    display: "grid",
    gridTemplateColumns: cols,
    gap,
    width: "100%",
    boxSizing: "border-box",
    ...style,
  }}>{children}</div>
);

function calcularRango(periodo) {
  const hoy = new Date();
  let desde = new Date();
  if (periodo === "dia") { desde = new Date(hoy); }
  else if (periodo === "semana") {
    desde = new Date(hoy);
    const dia = desde.getDay();
    desde.setDate(hoy.getDate() - (dia === 0 ? 6 : dia - 1));
  }
  else if (periodo === "mes")  { desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1); }
  else if (periodo === "anio") { desde = new Date(hoy.getFullYear(), 0, 1); }
  return {
    desde: desde.toISOString().split("T")[0],
    hasta: hoy.toISOString().split("T")[0],
  };
}

function KpiCard({ label, value, icon, color, sub, onClick }) {
  return (
    <Paper onClick={onClick} sx={{
      p: 2, borderRadius: 2, cursor: onClick ? "pointer" : "default",
      borderTop: `3px solid ${color}`, height: "100%", boxSizing: "border-box",
      "&:hover": onClick ? { boxShadow: 4 } : {}, transition: "box-shadow .2s",
    }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: "text.secondary" }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color, mt: 0.25, lineHeight: 1.2 }}>{value}</Typography>
          {sub && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{sub}</Typography>}
        </Box>
        <span style={{ fontSize: 24 }}>{icon}</span>
      </Box>
    </Paper>
  );
}

function CrmKpi({ label, value, icon, color, sub }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, borderLeft: `4px solid ${color}`, height: "100%", boxSizing: "border-box" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5, bgcolor: `${color}18`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, flexShrink: 0,
        }}>{icon}</Box>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2, color }}>{value}</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}>{label}</Typography>
          {sub && <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>{sub}</Typography>}
        </Box>
      </Box>
    </Paper>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMINISTRADOR" || user?.role === "SUPER_ADMIN";

  const [periodo, setPeriodo] = useState("mes");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [loading, setLoading] = useState(true);
  const [finData, setFinData] = useState({ ventas: 0, compras: 0, resultado: 0, porCobrar: 0, porPagar: 0, facturas: [], grafico: [] });
  const [crm, setCrm] = useState(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (periodo !== "personalizado") {
      const r = calcularRango(periodo);
      setFechaDesde(r.desde);
      setFechaHasta(r.hasta);
    }
  }, [periodo]);

  useEffect(() => {
    if (fechaDesde && fechaHasta) {
      cargarFinanciero(fechaDesde, fechaHasta);
      cargarCRM(fechaDesde, fechaHasta);
    }
  }, [fechaDesde, fechaHasta]);

  const cargarFinanciero = async (desde, hasta) => {
    setLoading(true);
    try {
      // Todas las llamadas en paralelo — antes eran secuenciales
      const [facturasRaw, comprasRaw, cxcRaw, cxpRaw] = await Promise.all([
        fetch(`${API}/api/facturas`, { headers }).then(r => r.json()).then(d => Array.isArray(d) ? d : []).catch(() => []),
        fetch(`${API}/api/contabilidad/compras`, { headers }).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API}/api/cuentas-cobrar/resumen-cartera`, { headers }).then(r => r.json()).catch(() => ({})),
        fetch(`${API}/api/cuentas-pagar/resumen-obligaciones`, { headers }).then(r => r.json()).catch(() => ({})),
      ]);

      const facFiltradas = facturasRaw.filter(f => {
        const estado = (f.estadoSRI || f.estado_sri || f.estado || "").toUpperCase();
        if (estado === "ANULADA" || estado === "ANULADO") return false;
        const raw = f.fechaEmision || f.fechaRegistro || f.fecha || "";
        let fecha = raw.includes("/") ? (() => { const p = raw.split("/"); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : ""; })() : raw.substring(0, 10);
        return !fecha || fecha.length < 10 || (fecha >= desde && fecha <= hasta);
      });

      const totalVentas  = facFiltradas.reduce((s, f) => s + parseFloat(f.totales?.valorPagar || f.totales?.importeTotal || 0), 0);
      const totalCompras = (Array.isArray(comprasRaw) ? comprasRaw : [])
        .filter(c => { const f = (c.fecha_emision || c.fecha || c.created_at || "").substring(0, 10); return !f || (f >= desde && f <= hasta); })
        .reduce((s, c) => s + parseFloat(c.totales?.importeTotal || c.total || 0), 0);

      const porCobrar = parseFloat((cxcRaw.data || cxcRaw).saldo_pendiente || 0);
      const porPagar  = parseFloat((cxpRaw.data || cxpRaw).saldo_pendiente || 0);

      const anio = new Date().getFullYear();
      const grafico = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"].map((mes, i) => {
        const ventas = facturasRaw
          .filter(f => { const e = (f.estadoSRI || "").toUpperCase(); if (e === "ANULADA" || e === "ANULADO") return false; const raw = f.fechaEmision || f.fechaRegistro || ""; let d; if (raw.includes("/")) { const p = raw.split("/"); d = new Date(+p[2], +p[1]-1, +p[0]); } else { d = new Date(raw); } return d.getFullYear() === anio && d.getMonth() === i; })
          .reduce((s, f) => s + parseFloat(f.totales?.valorPagar || 0), 0);
        return { mes, ventas: parseFloat(ventas.toFixed(2)) };
      });

      setFinData({ ventas: totalVentas, compras: totalCompras, resultado: totalVentas - totalCompras, porCobrar, porPagar, facturas: facFiltradas.slice(0, 8), grafico });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const cargarCRM = async (desde, hasta) => {
    try { const d = await fetch(`${API}/api/crm/estadisticas?desde=${desde}&hasta=${hasta}`, { headers }).then(r => r.json()); if (d.success) setCrm(d); } catch (_) {}
  };

  const fmt = n => `$${parseFloat(n || 0).toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading && !crm) return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress /></Box>;

  const canalData = crm ? [
    { name: "WhatsApp", value: crm.conversaciones.whatsapp, fill: "#25d366" },
    { name: "Chat Web",  value: crm.conversaciones.web,      fill: "#005eff" },
  ].filter(d => d.value > 0) : [];

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "#f5f6fa", py: 3, px: { xs: 2, sm: 3, md: 4 }, boxSizing: "border-box" }}>

      {/* HEADER */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>Panel de Control</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" size="small" onClick={() => navigate("/crear-factura")}
            sx={{ bgcolor: "#FF7A00", "&:hover": { bgcolor: "#e06000" }, textTransform: "none", fontWeight: 600 }}>
            + Facturar
          </Button>
          <Button variant="outlined" size="small" onClick={() => navigate("/compras")} sx={{ textTransform: "none" }}>+ Compra</Button>
          <Button variant="outlined" size="small" onClick={() => navigate("/crm/bandeja")} sx={{ textTransform: "none" }}>💬 Bandeja</Button>
        </Box>
      </Box>

      {/* PERÍODO */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <ToggleButtonGroup value={periodo} exclusive onChange={(_, v) => v && setPeriodo(v)} size="small">
          {[["dia","Día"],["semana","Semana"],["mes","Mes"],["anio","Año"],["personalizado","Personalizado"]].map(([v, l]) => (
            <ToggleButton key={v} value={v} sx={{ px: 1.5, fontSize: 12 }}>{l}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        {periodo === "personalizado" && <>
          <TextField size="small" type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} sx={{ width: 140 }} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} sx={{ width: 140 }} InputLabelProps={{ shrink: true }} />
        </>}
        <Chip label={`${fechaDesde} → ${fechaHasta}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
        {loading && <CircularProgress size={16} />}
      </Box>

      {/* FILA 1 — KPIs FINANCIEROS */}
      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "text.secondary", mb: 1 }}>Finanzas</Typography>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, width: "100%", marginBottom: 24 }}>
        {[
          { label: "Ventas",     value: fmt(finData.ventas),                       icon: "💰", color: "#2e7d32", path: "/facturaslist" },
          { label: "Compras",    value: fmt(finData.compras),                       icon: "🛒", color: "#c62828", path: "/compras" },
          { label: "Resultado",  value: fmt(finData.resultado),                     icon: "📈", color: finData.resultado >= 0 ? "#1565c0" : "#b71c1c" },
          { label: "Por Cobrar", value: fmt(finData.porCobrar),                     icon: "⏳", color: "#f57c00", path: "/cuentas-cobrar" },
          { label: "Por Pagar",  value: fmt(finData.porPagar),                      icon: "💸", color: "#7b1fa2", path: "/cuentas-pagar" },
          { label: "Flujo Neto", value: fmt(finData.porCobrar - finData.porPagar), icon: "🏦", color: "#00695c" },
        ].map(k => <KpiCard key={k.label} {...k} onClick={k.path ? () => navigate(k.path) : undefined} />)}
      </div>

      {/* FILA 2 — KPIs CRM */}
      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "text.secondary", mb: 1 }}>CRM & Comunicación</Typography>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, width: "100%", marginBottom: 24 }}>
        {[
          { label: "Conversaciones abiertas", value: crm?.conversaciones.abiertas ?? "—",          icon: "💬", color: "#1976d2", sub: crm ? `${crm.conversaciones.total_periodo} en período` : "" },
          { label: "Leads nuevos",            value: crm?.contactos.nuevos_periodo ?? "—",           icon: "🎯", color: "#FF7A00", sub: crm ? `${crm.contactos.chat_web} desde chat` : "" },
          { label: "Respuestas IA",           value: crm?.mensajes.de_ia ?? "—",                   icon: "🤖", color: "#7b1fa2", sub: crm ? `${crm.mensajes.tasa_ia_pct}% del total` : "" },
          { label: "Requieren humano",        value: crm?.conversaciones.requieren_humano ?? "—",   icon: "🙋", color: "#e53935", sub: crm ? `${crm.conversaciones.no_asignadas} sin asignar` : "" },
          { label: "Mensajes no leídos",      value: crm?.conversaciones.mensajes_no_leidos ?? "—", icon: "🔔", color: "#f57c00", sub: crm ? `⏱ ${crm.tiempo_respuesta_promedio_min} min prom.` : "" },
          { label: "Total contactos",         value: crm?.contactos.total ?? "—",                  icon: "🤝", color: "#00695c", sub: crm ? `${crm.mensajes.de_contactos} mensajes recibidos` : "" },
        ].map(k => <CrmKpi key={k.label} {...k} />)}
      </div>

      {/* FILA 3 — GRÁFICAS */}
      <div style={{ display: "grid", gridTemplateColumns: "60% 38%", gap: 16, width: "100%", marginBottom: 24, alignItems: "start" }}>
        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={700} gutterBottom>📊 Ventas por Mes ({new Date().getFullYear()})</Typography>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={finData.grafico} margin={{ left: 8, right: 8, top: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mes" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={v => `$${v}`} width={60} />
              <Tooltip formatter={v => [`$${v.toFixed(2)}`, "Ventas"]} />
              <Line type="monotone" dataKey="ventas" stroke="#2e7d32" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={700} gutterBottom>💬 Conversaciones (últimos 30 días)</Typography>
          {crm?.por_dia?.some(d => d.conversaciones > 0 || d.leads > 0) ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={crm.por_dia.filter((_, i) => i % 3 === 0)} barSize={10} margin={{ left: 0, right: 4, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" fontSize={10} />
                <YAxis fontSize={10} allowDecimals={false} width={24} />
                <Tooltip />
                <Bar dataKey="conversaciones" fill="#1976d2" name="Conversaciones" radius={[2,2,0,0]} />
                <Bar dataKey="leads" fill="#FF7A00" name="Leads" radius={[2,2,0,0]} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 220, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ fontSize: 38 }}>💬</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Sin conversaciones aún</Typography>
              <Button size="small" sx={{ mt: 1, textTransform: "none" }} onClick={() => navigate("/crm/bandeja")}>Ver bandeja</Button>
            </Box>
          )}
        </Paper>
      </div>

      {/* FILA 4 — FACTURAS + ACTIVIDAD CRM */}
      <div style={{ display: "grid", gridTemplateColumns: "55% 43%", gap: 16, width: "100%", marginBottom: 24, alignItems: "start" }}>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Typography variant="body2" fontWeight={700}>🧾 Últimas Facturas</Typography>
            <Button size="small" onClick={() => navigate("/facturaslist")} sx={{ textTransform: "none", fontSize: 11 }}>Ver todas →</Button>
          </Box>
          {finData.facturas.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}><Typography color="text.secondary" variant="body2">No hay facturas en este período</Typography></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f9fafb" }}>
                    {["Cliente","Número","Monto","Fecha","Estado"].map(h => (
                      <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, py: 0.75 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {finData.facturas.map((f, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: 12, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.cliente?.nombre || "—"}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>{f.secuencial ? String(f.secuencial).padStart(9, "0") : "—"}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>${parseFloat(f.totales?.valorPagar || 0).toFixed(2)}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{(f.fechaEmision || "").substring(0, 10)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={f.estadoSRI || "Pendiente"}
                          color={f.estadoSRI === "AUTORIZADO" ? "success" : "warning"}
                          variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Box>
          <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
              <Typography variant="body2" fontWeight={700}>💬 Actividad CRM</Typography>
              <Button size="small" onClick={() => navigate("/crm/bandeja")} sx={{ textTransform: "none", fontSize: 11 }}>Bandeja →</Button>
            </Box>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              {[
                { label: "WhatsApp",      val: crm?.conversaciones.whatsapp ?? 0,        color: "#25d366", icon: "📱" },
                { label: "Chat Web",      val: crm?.conversaciones.web ?? 0,              color: "#005eff", icon: "🌐" },
                { label: "Cerradas",      val: crm?.conversaciones.cerradas_periodo ?? 0, color: "#4caf50", icon: "✅" },
                { label: "Msj. clientes", val: crm?.mensajes.de_contactos ?? 0,           color: "#ff9800", icon: "✉️" },
              ].map(item => (
                <Box key={item.label} sx={{
                  p: 1.25, borderRadius: 1.5, textAlign: "center",
                  bgcolor: `${item.color}12`, border: `1px solid ${item.color}28`,
                }}>
                  <Typography sx={{ fontSize: 18 }}>{item.icon}</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: item.color, lineHeight: 1.2 }}>{item.val}</Typography>
                  <Typography sx={{ fontSize: 10, color: "text.secondary" }}>{item.label}</Typography>
                </Box>
              ))}
            </div>
            {canalData.length > 0 && canalData.map(c => (
              <Box key={c.name} sx={{ mb: 0.75 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="caption">{c.name}</Typography>
                  <Typography variant="caption" fontWeight={600}>{c.value}</Typography>
                </Box>
                <LinearProgress variant="determinate"
                  value={Math.min(100, (c.value / Math.max(1, crm.conversaciones.total_periodo)) * 100)}
                  sx={{ height: 5, borderRadius: 3, bgcolor: "#f0f0f0", "& .MuiLinearProgress-bar": { bgcolor: c.fill } }} />
              </Box>
            ))}
          </Paper>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={700} gutterBottom>⚡ Accesos Rápidos</Typography>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[["🧾 Nueva Factura","/crear-factura"],["🛒 Nueva Compra","/compras"],["🎯 Ver Leads","/crm/contactos"],["📊 Reportes","/contabilidad/reportes"]].map(([label, p]) => (
                <Button key={p} fullWidth variant="outlined" size="small" onClick={() => navigate(p)}
                  sx={{ justifyContent: "flex-start", textTransform: "none", fontSize: 11.5, py: 0.75 }}>
                  {label}
                </Button>
              ))}
            </div>
          </Paper>
        </Box>
      </div>

      {/* FILA 5 — AGENTES + PIPELINE (solo ADMIN) */}
      {isAdmin && (
        <div style={{ display: "grid", gridTemplateColumns: "55% 43%", gap: 16, width: "100%", marginBottom: 16, alignItems: "start" }}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={700} gutterBottom>👤 Rendimiento de Agentes</Typography>
            {crm?.agentes?.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f9fafb" }}>
                    {["Agente","Conversaciones","Mensajes","Actividad"].map(h => (
                      <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, py: 0.75 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {crm.agentes.map((a, i) => {
                    const maxC = crm.agentes[0]?.conversaciones_atendidas || 1;
                    return (
                      <TableRow key={a.agente_id} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: i === 0 ? "#FF7A00" : "#1976d2" }}>
                              {(a.agente_nombre || "A").charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{a.agente_nombre}</Typography>
                              {i === 0 && <Chip label="Top" size="small" sx={{ height: 16, fontSize: 9, bgcolor: "#FF7A00", color: "#fff" }} />}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>{a.conversaciones_atendidas}</TableCell>
                        <TableCell sx={{ color: "text.secondary", textAlign: "center" }}>{a.mensajes_enviados}</TableCell>
                        <TableCell sx={{ minWidth: 100 }}>
                          <LinearProgress variant="determinate"
                            value={Math.round((a.conversaciones_atendidas / maxC) * 100)}
                            sx={{ height: 6, borderRadius: 3, "& .MuiLinearProgress-bar": { bgcolor: i === 0 ? "#FF7A00" : "#1976d2" } }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography color="text.secondary" variant="body2">Sin datos de agentes en este período</Typography>
              </Box>
            )}
          </Paper>
          <ForecastWidget navigate={navigate} headers={headers} />
        </div>
      )}

    </Box>
  );
}

function ForecastWidget({ navigate, headers }) {
  const [forecast, setForecast] = useState(null);
  const API = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    fetch(`${API}/api/crm/oportunidades/forecast`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => d?.success && setForecast(d.data))
      .catch(() => {});
  }, []);

  const fmt = n => `$${parseFloat(n || 0).toLocaleString("es-EC", { minimumFractionDigits: 0 })}`;

  return (
    <Paper sx={{ p: 2, borderRadius: 2, height: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography variant="body2" fontWeight={700}>🔥 Pipeline de Ventas</Typography>
        <Button size="small" onClick={() => navigate("/crm/pipeline")} sx={{ textTransform: "none", fontSize: 11 }}>Ver pipeline</Button>
      </Box>
      {forecast ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
            <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "#1976d218", border: "1px solid #1976d230", textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} color="primary">{fmt(forecast.total_pipeline)}</Typography>
              <Typography variant="caption" color="text.secondary">Valor total pipeline</Typography>
            </Box>
            <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "#FF7A0018", border: "1px solid #FF7A0030", textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: "#FF7A00" }}>{fmt(forecast.forecast)}</Typography>
              <Typography variant="caption" color="text.secondary">Forecast ponderado</Typography>
            </Box>
          </div>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.75, display: "block" }}>
            Por etapa ({forecast.cantidad_abiertas} oportunidades)
          </Typography>
          {Object.values(forecast.por_etapa || {}).slice(0, 5).map(e => (
            <Box key={e.nombre} sx={{ mb: 0.75 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: e.color }} />
                  <Typography variant="caption">{e.nombre}</Typography>
                </Box>
                <Typography variant="caption" fontWeight={600}>{e.cantidad} · {fmt(e.valor)}</Typography>
              </Box>
              <LinearProgress variant="determinate"
                value={Math.min(100, (e.valor / Math.max(1, forecast.total_pipeline)) * 100)}
                sx={{ height: 5, borderRadius: 3, bgcolor: "#f0f0f0", "& .MuiLinearProgress-bar": { bgcolor: e.color } }} />
            </Box>
          ))}
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography sx={{ fontSize: 38, mb: 1 }}>🔥</Typography>
          <Typography variant="body2" color="text.secondary">Sin oportunidades en el pipeline</Typography>
          <Button size="small" onClick={() => navigate("/crm/pipeline")} sx={{ mt: 1, textTransform: "none" }}>Crear oportunidad</Button>
        </Box>
      )}
    </Paper>
  );
}
